import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categories, categoryMap } from '@/mocks/categories';
import { getCommentsByArticleId } from '@/mocks/comments';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-black text-foreground-950 mb-4">文章不存在</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categoryMap[article.category];
  const relatedArticles = getRelatedArticles(article, 4);
  const articleComments = getCommentsByArticleId(article.id);

  const keywords = Array.from(new Set([category?.name, article.category, ...article.tags])).join(',');

  return (
    <>
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.summary,
              author: { '@type': 'Person', name: article.author },
              datePublished: article.publishDate,
              image: article.imageUrl,
              url: `https://ryo7a.com/article/${article.id}`,
              publisher: {
                '@type': 'Organization',
                name: '每日吃瓜',
              },
              keywords: keywords,
              articleSection: category?.name || '',
            }),
          }}
        />

        <section className="relative min-h-[450px] md:min-h-[550px] flex items-center overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, oklch(0.97 0.015 85) 0%, oklch(0.94 0.06 60) 40%, oklch(0.88 0.08 40) 100%)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20" />

          <div className="relative z-10 w-full px-4 md:px-6 pt-24 pb-10">
            <div className="max-w-[1280px] mx-auto">
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', path: '/' },
                    ...(category ? [{ label: category.name, path: `/category/${category.slug}` }] : []),
                    { label: article.title },
                  ]}
                />
              </div>

              <div className="flex flex-col lg:flex-row items-start gap-8">
                <div className="lg:w-[60%] flex flex-col gap-5">
                  {category && (
                    <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-background-50/80 backdrop-blur-sm rounded-full text-xs font-medium text-primary-600 whitespace-nowrap">
                      <i className={`${category.icon}`} />
                      {category.name}
                    </span>
                  )}
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-black text-foreground-950 leading-tight">
                    {article.title}
                  </h1>
                </div>
                <div className="lg:w-[40%] flex flex-col gap-4 pt-4 lg:pt-20">
                  <p className="text-base text-foreground-700 leading-relaxed">
                    {article.summary}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-foreground-500">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-user-line" />
                      {article.author}
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-calendar-line" />
                      {article.publishDate}
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-eye-line" />
                      {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-6 z-10">
            <div className="max-w-[1280px] mx-auto">
              <div className="w-full h-[180px] md:h-[250px] rounded-2xl overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.imageAlt}
                  title={article.imageAlt}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <article className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="flex flex-wrap items-center gap-2 mb-10">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="prose prose-lg max-w-none">
                <p className="text-sm text-foreground-400 mb-8 text-center">
                  — 每日吃瓜独家报道 —
                </p>

                <div className="bg-background-100/70 rounded-2xl p-8 md:p-12">
                  {article.content ? (
                    article.content.split('\n\n').map((paragraph, idx) => (
                      <p
                        key={idx}
                        className={idx === 0
                          ? 'text-lg md:text-xl text-foreground-800 leading-relaxed mb-6'
                          : 'text-base md:text-lg text-foreground-700 leading-relaxed mb-6'
                        }
                      >
                        {paragraph}
                      </p>
                    ))
                  ) : (
                    <>
                      <p className="text-lg md:text-xl text-foreground-800 leading-relaxed mb-6">
                        {article.summary}
                      </p>
                      <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                        据知情人士透露，此事在圈内早已不是秘密。多位不愿具名的业内人士向每日吃瓜记者证实，
                        相关事件的发生时间比公开报道要早得多。
                      </p>
                      <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                        每日吃瓜记者经过多方核实与深入调查，独家获取了一系列关键证据。
                        从时间线来看，事件的发展脉络逐渐清晰——
                        从最初的蛛丝马迹到如今的实锤落地，整个过程堪称娱乐圈年度最精彩的大戏之一。
                      </p>
                      <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                        目前，相关方面尚未对此事做出正式回应。但据内部人士透露，
                        涉事方正在紧急商讨应对方案，预计在近期会有官方声明。
                        每日吃瓜将持续关注此事的最新进展，为各位吃瓜群众带来第一手消息。
                      </p>
                      <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                        网友们对此事的反应也相当激烈。相关话题阅读量已突破千万级别，
                        评论区更是一片沸腾。有网友表示："早就觉得不对劲，果然出事了"；
                        也有网友持观望态度："等官方回应再下结论"。无论如何，
                        这个瓜显然还没有结束，后续发展让我们拭目以待。
                      </p>
                    </>
                  )}
                </div>

                <p className="text-sm text-foreground-400 mt-10 text-center">
                  — 全文完 · 每日吃瓜持续追踪 —
                </p>
              </div>
            </div>
          </div>
        </article>

        {articleComments.length > 0 && (
          <section className="py-14 md:py-20 bg-foreground-950 relative overflow-hidden">
            <div className="absolute top-0 left-0 opacity-[0.02] pointer-events-none">
              <svg width="200" height="400" viewBox="0 0 200 400" fill="none">
                <path d="M20 0C20 0 80 50 50 120C20 190 100 180 130 240C160 300 100 350 100 400" stroke="currentColor" strokeWidth="1.5" className="text-background-50" />
                <path d="M60 0C60 0 120 40 90 130C60 220 140 200 170 260C200 320 130 370 130 400" stroke="currentColor" strokeWidth="1" className="text-background-50" opacity="0.6" />
              </svg>
            </div>

            <div className="w-full px-4 md:px-6 relative z-10">
              <div className="max-w-[1280px] mx-auto">
                <div className="mb-10">
                  <h2 className="text-3xl md:text-4xl font-heading font-black text-background-50">
                    瓜友<span className="text-primary-400">热评</span>
                  </h2>
                  <p className="text-sm text-foreground-400 mt-2">共 {articleComments.length} 条评论</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articleComments.map((comment) => (
                    <div
                      key={comment.id}
                      className="bg-foreground-900 rounded-xl p-6 flex flex-col gap-4"
                    >
                      <span className="text-4xl font-heading text-primary-400/20 leading-none">
                        &ldquo;
                      </span>
                      <p className="text-sm text-background-100 leading-relaxed flex-1">
                        {comment.content}
                      </p>
                      <div className="flex items-center justify-between pt-2 border-t border-foreground-800">
                        <div>
                          <p className="text-xs font-medium text-background-50">{comment.author}</p>
                          <p className="text-xs text-foreground-400 mt-0.5">
                            {comment.level} · {comment.publishDate}
                          </p>
                        </div>
                        <span className="flex items-center gap-1 text-xs text-primary-400">
                          <i className="ri-heart-fill" />
                          {comment.likes}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-[1280px] mx-auto">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-3xl md:text-4xl font-heading font-black text-foreground-950">
                      相关<span className="text-primary-500">爆料</span>
                    </h2>
                    <p className="text-sm text-foreground-500 mt-2">你可能还会感兴趣的内容</p>
                  </div>
                </div>

                <div className="flex gap-4 overflow-x-auto pb-4 -mr-4 md:-mr-6 scrollbar-hide"
                  style={{ scrollSnapType: 'x mandatory' }}
                >
                  {relatedArticles.map((ra) => {
                    const raCategory = categoryMap[ra.category];
                    return (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.id}`}
                        className="flex-shrink-0 w-[280px] md:w-[320px] bg-background-50 rounded-xl overflow-hidden cursor-pointer group border border-background-200/70 hover:border-background-300/60 transition-colors"
                        style={{ scrollSnapAlign: 'start' }}
                      >
                        <div className="relative h-[180px] overflow-hidden">
                          <img
                            src={ra.imageUrl}
                            alt={ra.imageAlt}
                            title={ra.imageAlt}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 flex flex-col gap-2">
                          {raCategory && (
                            <span className="inline-flex self-start px-2.5 py-0.5 bg-accent-100 text-accent-800 text-xs font-medium rounded-full whitespace-nowrap">
                              {raCategory.name}
                            </span>
                          )}
                          <h3 className="text-sm font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
                            {ra.title}
                          </h3>
                          <div className="flex items-center justify-between text-xs text-foreground-400">
                            <span>{ra.publishDate}</span>
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line" />
                              {ra.views > 10000 ? `${(ra.views / 10000).toFixed(1)}万` : ra.views}
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}
